/**
 * Material Transfers API Module
 * Follows exact same patterns as HarvestApi
 */

import { type RequestFunction } from './types'
import type {
  MaterialTransfer,
  MaterialTransferItem,
  CreateTransferInput,
  UpdateTransferInput,
  AddPalletInput,
  AddNoteInput,
  GeneratePDFResponse,
  PDFResponse,
  TransferStats,
  AvailablePalletsResponse,
  PaginatedTransfers,
  FilterParams,
  TransferNote,
} from './types'

export class TransfersApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // MATERIAL TRANSFERS MANAGEMENT
  // ============================================================================

  async getTransfers(params?: FilterParams & {
    sender_id?: number
    receiver_id?: number
    status?: string
    include_archived?: boolean
  }): Promise<PaginatedTransfers> {
    return this.request('/api/material-transfers/', { params })
  }

  async createTransfer(data: CreateTransferInput): Promise<MaterialTransfer> {
    return this.request('/api/material-transfers/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async getTransfer(id: number): Promise<MaterialTransfer> {
    return this.request(`/api/material-transfers/${id}/`)
  }

  async updateTransfer(id: number, data: UpdateTransferInput): Promise<MaterialTransfer> {
    return this.request(`/api/material-transfers/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteTransfer(id: number): Promise<void> {
    return this.request(`/api/material-transfers/${id}/`, {
      method: 'DELETE'
    })
  }

  // ============================================================================
  // TRANSFER STATUS MANAGEMENT
  // ============================================================================

  async updateStatus(id: number, status: string): Promise<MaterialTransfer> {
    return this.request(`/api/material-transfers/${id}/status/`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    })
  }

  async cancelTransfer(id: number): Promise<MaterialTransfer> {
    return this.request(`/api/material-transfers/${id}/cancel/`, {
      method: 'POST'
    })
  }

  // ============================================================================
  // PDF MANIFEST OPERATIONS
  // ============================================================================

  async generatePDF(id: number): Promise<GeneratePDFResponse> {
    return this.request(`/api/material-transfers/${id}/generate_pdf/`, {
      method: 'POST'
    })
  }

  async getPDF(id: number): Promise<PDFResponse> {
    return this.request(`/api/material-transfers/${id}/pdf/`)
  }

  // ============================================================================
  // TRANSFER ITEMS (PALLETS) MANAGEMENT
  // ============================================================================

  async getItems(transferId: number): Promise<MaterialTransferItem[]> {
    return this.request(`/api/material-transfers/${transferId}/items/`)
  }

  async addPallet(transferId: number, palletId: number): Promise<MaterialTransferItem> {
    return this.request(`/api/material-transfers/${transferId}/items/`, {
      method: 'POST',
      body: JSON.stringify({ pallet_id: palletId })
    })
  }

  async removeItem(transferId: number, itemId: number): Promise<void> {
    return this.request(`/api/material-transfers/${transferId}/items/${itemId}/`, {
      method: 'DELETE'
    })
  }

  // ============================================================================
  // PALLET AVAILABILITY
  // ============================================================================

  async getAvailablePallets(containerId: number, excludeTransferId?: number): Promise<AvailablePalletsResponse> {
    const params: any = { container_id: containerId }
    if (excludeTransferId) {
      params.exclude_transfer_id = excludeTransferId
    }
    return this.request('/api/pallets/available/', { params })
  }

  // ============================================================================
  // NOTES MANAGEMENT
  // ============================================================================

  async getNotes(transferId: number): Promise<TransferNote[]> {
    return this.request(`/api/material-transfers/${transferId}/notes/`)
  }

  async addNote(transferId: number, text: string): Promise<TransferNote> {
    return this.request(`/api/material-transfers/${transferId}/notes/`, {
      method: 'POST',
      body: JSON.stringify({ text })
    })
  }

  // ============================================================================
  // STATISTICS
  // ============================================================================

  async getStats(): Promise<TransferStats> {
    return this.request('/api/material-transfers/stats/')
  }
}
